/* SUMMARY
  * DEFAULT_TABLE_PAGE_SIZE / TABLE_PAGE_SIZE_OPTIONS: le contrat affiché par le profil
  * TABLE_PAGE_SIZE_EVENT: notification intra-onglet
  * isValidTablePageSize: garde-fou de lecture
  * readTablePageSize / writeTablePageSize: miroir local
*/

/**
 * Mémorisation du nombre de lignes par page choisi par l'utilisateur.
 *
 * Le serveur fait autorité : ce module n'est qu'un miroir local, lu de façon **synchrone** au
 * premier rendu des tableaux. Sans lui, chaque liste afficherait 20 lignes le temps d'un aller-
 * retour réseau puis se re-rendrait à 50 — un clignotement visible sur les quinze écrans paginés.
 * Le miroir ne sert jamais à décider de ce qu'on envoie, uniquement de ce qu'on affiche en
 * attendant la réponse du serveur.
 *
 * Ce qui est stocké, c'est un choix explicite, jamais le défaut. Écrire « 20 » figerait le défaut
 * d'aujourd'hui dans le navigateur de chaque utilisateur : le jour où le produit décide que le
 * défaut devient 25, seuls ceux qui n'ont jamais ouvert le réglage devraient suivre, et ils ne
 * suivraient pas. L'absence de clé est donc porteuse de sens — même raisonnement que
 * `exportFieldSelection.ts`, qui stocke les colonnes exclues et non les incluses.
 *
 * La clé n'est pas nominative. Si un utilisateur ferme l'onglet sans se déconnecter et qu'un autre
 * se connecte derrière, le premier rendu utilise la taille du précédent jusqu'à ce que la
 * réconciliation au démarrage arrive. C'est un choix : `exportFieldSelection.ts` accepte déjà
 * exactement ce résidu, la donnée qui fuite est un nombre de lignes, et l'écart se corrige en un
 * aller-retour. Préfixer par l'identifiant utilisateur obligerait les quinze appelants à le
 * transporter pour fermer une fenêtre d'une seconde.
 */

const STORAGE_PREFIX = "tablePreferences";

/** Clé du miroir. Préfixée comme `exportFields:` pour que le stockage reste lisible à l'œil nu. */
const STORAGE_KEY = `${STORAGE_PREFIX}:pageSize`;

/** Taille appliquée tant que l'utilisateur n'a rien choisi. C'est celle que treize écrans sur quinze utilisaient en dur. */
export const DEFAULT_TABLE_PAGE_SIZE = 20;

/** Les tailles proposées par le profil. Une liste blanche, pas un intervalle : voir `isValidTablePageSize`. */
export const TABLE_PAGE_SIZE_OPTIONS: readonly number[] = [10, 25, 50, 100];

/**
 * Événement émis à chaque écriture, pour les tableaux déjà montés du même onglet.
 *
 * L'événement natif `storage` ne se déclenche que dans les *autres* onglets ; sans ce doublon,
 * changer la préférence depuis son profil ne toucherait aucune liste ouverte derrière.
 */
export const TABLE_PAGE_SIZE_EVENT = "easytech:tablePageSizeChange";

/**
* isValidTablePageSize: la valeur est-elle une des tailles que le profil sait afficher.
*
* Liste blanche et non intervalle : un 37 stocké à la main passerait n'importe quel `min/max`, puis
* s'afficherait dans une liste déroulante qui ne le propose pas — l'utilisateur serait bloqué sur
* une taille qu'il ne peut ni voir sélectionnée ni changer.
*
* @param value - valeur candidate, typiquement issue du stockage ou du réseau
*/
export const isValidTablePageSize = (value: unknown): value is number =>
  typeof value === "number" && TABLE_PAGE_SIZE_OPTIONS.includes(value);

/**
* readTablePageSize: la taille à appliquer maintenant, sans attendre le réseau.
*
* Tolère un stockage absent, refusé ou corrompu. Un `NaN` ou un `0` qui remonterait jusqu'au
* `slice()` d'un tableau afficherait une liste vide sans rien expliquer : tout ce qui n'est pas une
* option connue retombe donc sur le défaut.
*/
export const readTablePageSize = (): number => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_TABLE_PAGE_SIZE;

    const parsed = Number.parseInt(raw, 10);

    return isValidTablePageSize(parsed) ? parsed : DEFAULT_TABLE_PAGE_SIZE;
  } catch {
    return DEFAULT_TABLE_PAGE_SIZE;
  }
};

/**
* writeTablePageSize: mémorise un choix, ou efface l'entrée quand on revient au défaut.
*
* `null` comme `DEFAULT_TABLE_PAGE_SIZE` effacent la clé plutôt que d'écrire une valeur : c'est ce
* qui garde « pas de choix » distinct de « a choisi 20 ».
*
* @param size - taille choisie, ou `null` pour revenir au défaut
*/
export const writeTablePageSize = (size: number | null): void => {
  try {
    if (size === null || size === DEFAULT_TABLE_PAGE_SIZE || !isValidTablePageSize(size)) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, String(size));
    }
  } catch {
    // Stockage plein ou refusé (navigation privée) : la préférence ne survivra pas au rechargement,
    // ce qui est acceptable — mais lever ici casserait le gestionnaire de clic du profil.
  }

  // Hors du try : même si l'écriture a échoué, les tableaux montés doivent se réaligner sur ce que
  // la lecture renverra désormais, sinon l'écran affiche une taille que plus personne ne stocke.
  try {
    window.dispatchEvent(new Event(TABLE_PAGE_SIZE_EVENT));
  } catch {
    // Environnement sans window (rendu hors navigateur) : rien à notifier.
  }
};
